// CreateScriptModal — the script create surface (Phase 5).
//
// Opened from a trending-topic card. Lets the operator pick a channel profile
// + target duration, then generate a script. When "시놉시스 먼저 보기" is on, it
// first calls the stateless synopses preview, shows 3 candidate cards, and
// drafts the chosen one (outline+hook carried into create). When off, it
// generates the full draft in one shot. On success it hands the new project id
// back so the caller can route to the editor.

import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Sparkles, X } from 'lucide-react'
import { api, ApiError } from '@/api/client'
import type { CreateScriptInput, SpikeView, Synopsis, SynopsisResult } from '@/api/types'
import { Button, Card, FormRow, Input, Select } from '@/admin-design/_common'

interface Props {
  topic: SpikeView
  onClose: () => void
  onCreated: (projectId: number) => void
}

export default function CreateScriptModal({ topic, onClose, onCreated }: Props) {
  const profilesQ = useQuery({ queryKey: ['channel-profiles'], queryFn: api.listProfiles })
  const [profileId, setProfileId] = useState<number | null>(null)
  const [duration, setDuration] = useState(60)
  const [interactive, setInteractive] = useState(false)
  const [synopsis, setSynopsis] = useState<SynopsisResult | null>(null)

  // Default the profile select to the first profile once loaded.
  const profiles = profilesQ.data ?? []
  const effectiveProfileId = profileId ?? profiles[0]?.id ?? null

  const baseInput = (): CreateScriptInput => ({
    profile_id: effectiveProfileId ?? 0,
    topic_id: topic.topic_id,
    target_duration_sec: duration > 0 ? duration : undefined,
  })

  const synopsesM = useMutation({
    mutationFn: api.synopses,
    onSuccess: (res) => setSynopsis(res),
  })
  const createM = useMutation({
    mutationFn: api.createScript,
    onSuccess: (res) => onCreated(res.project.id),
  })

  const busy = synopsesM.isPending || createM.isPending
  const canGenerate = Boolean(effectiveProfileId) && !busy

  const onGenerate = () => {
    if (!canGenerate) return
    if (interactive) synopsesM.mutate(baseInput())
    else createM.mutate(baseInput())
  }

  const onPick = (c: Synopsis) => {
    if (!synopsis || createM.isPending) return
    createM.mutate({ ...baseInput(), outline: synopsis.outline, hook: c.hook })
  }

  const err = synopsesM.isError
    ? errorMessage(synopsesM.error)
    : createM.isError
      ? errorMessage(createM.error)
      : null

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-black/40 p-4">
      <Card className="w-full max-w-lg shadow-xl">
        <Card.Body className="p-5">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-card-foreground">새 스크립트</h2>
              <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">토픽: {topic.title}</p>
            </div>
            <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground" aria-label="close">
              <X size={16} />
            </button>
          </div>

          {!synopsis ? (
            <div className="space-y-4">
              <FormRow label="Channel profile" required>
                <Select
                  value={effectiveProfileId ? String(effectiveProfileId) : ''}
                  onValueChange={(v) => setProfileId(Number(v))}
                  placeholder={profilesQ.isLoading ? '불러오는 중…' : '프로필 선택'}
                  options={profiles.map((p) => ({ label: p.name, value: String(p.id) }))}
                />
              </FormRow>

              <FormRow label="Target duration (sec)" help="0 = 기본값 (short 60s)">
                <Input
                  type="number"
                  min={0}
                  value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                />
              </FormRow>

              <label className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={interactive}
                  onChange={(e) => setInteractive(e.target.checked)}
                  className="h-4 w-4 rounded border-border"
                />
                시놉시스 3안을 먼저 보고 고르기
              </label>

              {err && <p className="text-sm text-destructive">{err}</p>}

              <div className="flex justify-end gap-2 border-t border-border pt-3">
                <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={busy}>
                  취소
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={onGenerate}
                  disabled={!canGenerate}
                  loading={busy}
                >
                  <Sparkles size={14} />
                  {interactive ? '시놉시스 보기' : '스크립트 생성'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">마음에 드는 시놉시스를 고르면 대본을 생성합니다.</p>
              {synopsis.candidates.map((c, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => onPick(c)}
                  disabled={createM.isPending}
                  className="block w-full rounded-md border border-border bg-card p-3 text-left shadow-sm transition hover:border-ring hover:bg-muted/30 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <p className="text-sm font-medium text-card-foreground">{c.hook}</p>
                  {c.premise && <p className="mt-1 text-xs text-muted-foreground">{c.premise}</p>}
                </button>
              ))}

              {err && <p className="text-sm text-destructive">{err}</p>}

              <div className="flex justify-between border-t border-border pt-3">
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={() => setSynopsis(null)}
                  disabled={createM.isPending}
                >
                  ← 다시
                </Button>
                {createM.isPending && <span className="text-xs text-muted-foreground">대본 생성 중…</span>}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message
  if (e instanceof Error) return e.message
  return 'unknown error'
}
